import { MatrixClient, RoomMember } from 'matrix-js-sdk/src/matrix';
import { useEffect, useRef } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';

import {
  DeferredInviteSnapshotStore,
  registerDeferredInviteMembershipListener,
  snapshotFromJoinedRoom,
} from './deferredInviteSnapshot';

const seedNewRoomsDuringDeferral = (
  matrixClient: MatrixClient,
  store: DeferredInviteSnapshotStore,
  shouldTrack: (member: RoomMember) => boolean,
  shouldTrackJoin: ((member: RoomMember) => boolean) | undefined,
  isRoomInBaseline: (roomId: string) => boolean
): void => {
  const myUserId = matrixClient.getUserId();
  if (!myUserId) return;

  matrixClient.getRooms().forEach((room) => {
    if (isRoomInBaseline(room.roomId)) return;

    const member = room.getMember(myUserId);
    if (!member) return;

    if (shouldTrack(member)) {
      store.trackRoom(room, myUserId);
      return;
    }

    if (shouldTrackJoin?.(member)) {
      const snapshot = snapshotFromJoinedRoom(room, myUserId);
      if (snapshot) store.track(snapshot);
    }
  });
};

const useDeferredInviteSnapshot = (
  isDeferred: boolean,
  shouldTrack: (member: RoomMember) => boolean,
  shouldTrackJoin?: (member: RoomMember) => boolean,
  isRoomInBaseline?: (roomId: string) => boolean
): DeferredInviteSnapshotStore => {
  const { matrixClient } = useMatrixContext();
  const storeRef = useRef(new DeferredInviteSnapshotStore());
  const shouldTrackRef = useRef(shouldTrack);
  shouldTrackRef.current = shouldTrack;
  const shouldTrackJoinRef = useRef(shouldTrackJoin);
  shouldTrackJoinRef.current = shouldTrackJoin;
  const isRoomInBaselineRef = useRef(isRoomInBaseline);
  isRoomInBaselineRef.current = isRoomInBaseline;
  const wasDeferredRef = useRef(isDeferred);

  useEffect(() => {
    if (isDeferred && !wasDeferredRef.current && isRoomInBaselineRef.current) {
      seedNewRoomsDuringDeferral(
        matrixClient,
        storeRef.current,
        (member) => shouldTrackRef.current(member),
        shouldTrackJoinRef.current ? (member) => shouldTrackJoinRef.current!(member) : undefined,
        (roomId) => isRoomInBaselineRef.current!(roomId)
      );
    }
    wasDeferredRef.current = isDeferred;
  }, [matrixClient, isDeferred]);

  useEffect(
    () =>
      registerDeferredInviteMembershipListener(
        matrixClient,
        isDeferred,
        storeRef.current,
        (member) => shouldTrackRef.current(member),
        shouldTrackJoinRef.current ? (member) => shouldTrackJoinRef.current!(member) : undefined
      ),
    [matrixClient, isDeferred]
  );

  return storeRef.current;
};

export default useDeferredInviteSnapshot;
